
import { defineComponent } from 'vue';
import {
  Matomo,
  ContentBlock,
  ContentTable,
  SiteRef,
  translate,
  Site,
  externalRawLink,
  Notification,
  Tooltips,
} from 'CoreHome';
import { Field } from 'CorePluginsAdmin';
import { getDisplayApiKey } from '../utilities';
import SearchEngineNavigation from '../Navigation/Navigation.vue';
import WebsitesAvailableModal from '../components/WebsitesAvailableModal.vue';
import CredentialsErrorBlock from '../components/CredentialsErrorBlock.vue';

interface BingConfigState {
  removeAccountConfigName: string;
  removeAccountName: string;
  isAddingMeasurable: boolean;
  currentSiteToAdd: SiteRef;
  bingAccountAndUrlToAdd: string|null;
  apiKeyToAdd: string;
}

interface BingAccount {
  apiKey: string;
  username: string;
  created_formatted: string;
  urls: Record<string, boolean>;
  hasError?: string;
}

interface ConfiguredBingInfo {
  bingSiteUrl: string;
}

export default defineComponent({
  props: {
    configuredMeasurables: {
      type: Object,
      required: true,
    },
    accounts: {
      type: Object,
      required: true,
    },
    sitesInfos: {
      type: Object,
      required: true,
    },
    providerName: {
      type: String,
      default: '',
    },
    currentSite: {
      type: Object,
      required: true,
    },
    urlOptions: {
      type: [Object, Array],
      required: true,
    },
    error: String,
    apikey: String,
    formNonce: String,
    addBingSiteConfigNonce: String,
    removeBingSiteConfigNonce: String,
    removeBingAccountNonce: String,
    countOfAccountsWithAccess: Number,
    userIsSuperUser: String,
    hasCredentialsError: Boolean,
    resetCredentialsUrl: String,
  },
  data(): BingConfigState {
    return {
      removeAccountConfigName: '',
      removeAccountName: '',
      isAddingMeasurable: false,
      currentSiteToAdd: this.currentSite as SiteRef,
      bingAccountAndUrlToAdd: null,
      apiKeyToAdd: this.apikey || '',
    };
  },
  components: {
    SearchEngineNavigation,
    ContentBlock,
    CredentialsErrorBlock,
    Field,
    Notification,
    WebsitesAvailableModal,
  },
  directives: {
    ContentTable,
    Tooltips,
  },
  methods: {
    removeAccountConfig(siteId: string|number, event: Event) {
      const siteInfos = this.sitesInfos as Record<string|number, Site>;
      this.removeAccountConfigName = siteInfos[siteId].name;

      Matomo.helper.modalConfirm(this.$refs.confirmRemoveAccountConfig as HTMLElement, {
        yes() {
          (event.target as HTMLFormElement).submit();
        },
      });
    },
    getDisplayApiKey,
    removeAccount(account: BingAccount, event: Event) {
      this.removeAccountName = this.getDisplayApiKey(account.apiKey);
      Matomo.helper.modalConfirm(this.$refs.confirmDeleteAccount as HTMLElement, {
        yes() {
          (event.target as HTMLFormElement).submit();
        },
      });
    },
    accountHasAvailableSites(account: BingAccount) {
      return Object.values(account.urls).some((verified) => verified);
    },
    getAvailableWebsites(account: BingAccount): string[] {
      return Object.entries(account.urls)
        .filter(([, verified]) => verified)
        .map(([url]) => url)
        .sort((first, second) => first.localeCompare(second, undefined, { sensitivity: 'base' }));
    },
    getUnverifiedWebsites(account: BingAccount): string[] {
      return Object.entries(account.urls)
        .filter(([, verified]) => !verified)
        .map(([url]) => url)
        .sort((first, second) => first.localeCompare(second, undefined, { sensitivity: 'base' }));
    },
    getAdditionalWebsitesLinkLabel(websites: string[]): string {
      return `+${websites.length - 1}`;
    },
  },
  computed: {
    unverifiedWebsitesModalTitle() {
      return translate('SearchEngineKeywordsPerformance_UnverifiedWebsites');
    },
    hasApiKeyError() {
      return typeof this.error !== 'undefined' && this.error !== null;
    },
    showCredentialsError() {
      return !!this.userIsSuperUser && !!this.hasCredentialsError;
    },
    configuredMeasurablesToDisplay() {
      const entries = Object.entries(
        this.configuredMeasurables as Record<string | number, ConfiguredBingInfo>,
      );

      return Object.fromEntries(
        entries.filter(([, config]) => {
          const [account] = config.bingSiteUrl.split('##');
          return !!this.accounts[account];
        }).map(([siteId, config]) => {
          const [account, url] = config.bingSiteUrl.split('##');
          const { apiKey } = this.accounts[account];

          return [
            siteId,
            {
              ...config,
              account,
              url,
              apiKeyDisplay: this.getDisplayApiKey(apiKey),
            },
          ];
        }),
      );
    },
    bingApiKeyInstructionText() {
      return translate(
        'SearchEngineKeywordsPerformance_BingAPIKeyInstruction',
        '<a href="https://www.bing.com/webmasters" target="_new" rel="noreferrer noopener">',
        '</a>',
        '',
        '',
      );
    },
    visitBingApiKeyHowTo() {
      const link = externalRawLink('https://matomo.org/faq/reports/import-bing-and-yahoo-search-keywords-into-matomo/');
      return translate(
        'SearchEngineKeywordsPerformance_VisitBingApiKeyHowTo',
        `<a target="_blank" href="${link}" rel="noreferrer noopener">`,
        '</a>',
      );
    },
    accountsToDisplay() {
      const asArray = Object.entries(this.accounts);
      const filtered = asArray.filter(([, value]) => value.hasAccess);

      return Object.fromEntries(filtered);
    },
  },
});
